import React from 'react';
import styled from 'styled-components';
import { SkeletonLoader } from './BasicSkeletonShapes';
import { colorBackground, colorBorder } from '../../../utils/palette';

const MainWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: ${colorBackground};
  overflow: hidden;
`;

const HorizontalHeader = styled.div`
  height: 80px;
  width: 100%;
  background: ${colorBackground};
  border-bottom: 1px solid ${colorBorder};
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const SidebarWrapper = styled.div`
  width: 280px;
  min-height: calc(100vh - 80px); // Subtract header height
  background: ${colorBackground};
  padding: 20px 0;
  border-right: 1px solid ${colorBorder};
  z-index: 0;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 24px;
`;

const ProjectHeader = styled.div`
  margin-bottom: 24px;
`;

const TabsWrapper = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
`;

const TableWrapper = styled.div`
  margin-top: 20px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr 150px 150px 150px 150px;
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid ${colorBorder};
  background: ${colorBackground};
`;

const TableRow = styled(TableHeader)`
  border-bottom: 1px solid ${colorBorder};
  background: ${colorBackground};
  padding: 12px 24px;
`;

export const MainLoading = () => {
  return (
    <MainWrapper>
      <HorizontalHeader>
        <div
          style={{ display: 'flex', gap: '16px', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}
        >
          <SkeletonLoader
            isLoading={true}
            height='32px'
            width='350px'
            bgColor='rgba(50, 88, 183, 0.1)'
            waveColor='rgba(38, 37, 37, 0.3)'
          />
          <SkeletonLoader
            isLoading={true}
            height='32px'
            width='200px'
            bgColor='rgba(50, 88, 183, 0.1)'
            waveColor='rgba(38, 37, 37, 0.3)'
          />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginLeft: '16px' }}>
          {/*------------------- User profile and actions -------------------*/}
          <SkeletonLoader
            isLoading={true}
            height='32px'
            width='150px'
            bgColor='rgba(50, 88, 183, 0.1)'
            waveColor='rgba(38, 37, 37, 0.3)'
          />
          <SkeletonLoader
            isLoading={true}
            height='32px'
            width='32px'
            shape='circle'
            bgColor='rgba(50, 88, 183, 0.1)'
            waveColor='rgba(38, 37, 37, 0.3)'
          />
        </div>
      </HorizontalHeader>

      <ContentWrapper>
        {/*------------------- Sidebar -------------------*/}
        <SidebarWrapper>
          {/*------------------- Logo -------------------*/}
          <div style={{ padding: '0 16px', marginBottom: '32px' }}>
            <SkeletonLoader
              isLoading={true}
              height='32px'
              width='140px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
            />
          </div>

          {/*------------------- Navigation Items -------------------*/}
          <div style={{ padding: '0 16px' }}>
            {Array.from({ length: 15 }).map((_, index) => (
              <div
                key={index}
                style={{ marginBottom: '16px' }}
              >
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width={index % 3 === 0 ? '180px' : '140px'}
                  index={index}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
              </div>
            ))}
          </div>
        </SidebarWrapper>

        {/*------------------- Main Content Area -------------------*/}
        <MainContent>
          {/*------------------- Project Header -------------------*/}
          <ProjectHeader>
            <SkeletonLoader
              isLoading={true}
              height='32px'
              width='200px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
              style={{ marginBottom: '16px' }}
            />
          </ProjectHeader>

          {/*------------------- Tabs -------------------*/}
          <TabsWrapper>
            <SkeletonLoader
              isLoading={true}
              height='32px'
              width='120px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
            />
            <div style={{ float: 'right' }}>
              <SkeletonLoader
                isLoading={true}
                height='32px'
                width='140px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </div>
          </TabsWrapper>

          {/*------------------- Action Buttons -------------------*/}
          <ActionBar>
            <SkeletonLoader
              isLoading={true}
              height='36px'
              width='100px'
              bgColor='rgba(50, 88, 183, 0.1)'
              waveColor='rgba(38, 37, 37, 0.3)'
            />
            <div style={{ float: 'right' }}>
              <SkeletonLoader
                isLoading={true}
                height='36px'
                width='140px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </div>
          </ActionBar>

          {/*------------------- Table -------------------*/}
          <TableWrapper>
            {/*------------------- Table Header -------------------*/}
            <TableHeader>
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='100px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
            </TableHeader>

            {/*------------------- Table Rows -------------------*/}
            {Array.from({ length: 4 }).map((_, rowIndex) => (
              <TableRow key={rowIndex}>
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='100px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='20px'
                  width='80px'
                  index={rowIndex}
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
              </TableRow>
            ))}

            {/*------------------- Pagination -------------------*/}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
              }}
            >
              <SkeletonLoader
                isLoading={true}
                height='24px'
                width='120px'
                bgColor='rgba(50, 88, 183, 0.1)'
                waveColor='rgba(38, 37, 37, 0.3)'
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
                <SkeletonLoader
                  isLoading={true}
                  height='24px'
                  width='32px'
                  bgColor='rgba(50, 88, 183, 0.1)'
                  waveColor='rgba(38, 37, 37, 0.3)'
                />
              </div>
            </div>
          </TableWrapper>
        </MainContent>
      </ContentWrapper>
    </MainWrapper>
  );
};
